/**
 * Date Conversion Utilities
 *
 * This file contains utility functions for transforming dates between different formats and types.
 * These functions handle FORMAT and TYPE conversions without changing the underlying date value.
 * Use these utilities when you need to:
 * - Format dates to string patterns (e.g., Date → "2024-01-15", "Jan 15, 2024", ISO string)
 * - Convert between Date objects and string representations (e.g., string → Date, Date → string)
 * - Parse and normalize date strings from various formats (e.g., "15/01/2024" → "2024-01-15")
 * - Display dates in human-readable or localized formats (e.g., "Today at 3:45 PM", "3 hours ago")
 * - Format durations in human-friendly strings (e.g., { hours: 2, minutes: 30 } → "2 hours, 30 minutes")
 *
 * Key distinction: These functions change HOW a date is represented, not WHAT date it represents.
 */
import {
  DateTime as LuxonDateTime,
  Duration as LuxonDuration,
  type DurationUnit as LuxonDurationUnit,
  type DateTimeFormatOptions,
  type ToHumanDurationOptions,
  type DurationObjectUnits,
} from 'luxon'
import { TimeFormat } from '@/utils/time.js'
import type { SupportedLocaleDateFormats } from '@/composables/i18next.js'
/**
 *  Explicitly importing functions is necessary since they're utilized
 *  within this file. This file also operates within the Playwright
 *  execution context, where auto imports aren't available as they are in Nuxt.
 */
import { getDateTime } from '@/utils/date/getter.js'
import { pad } from '@/utils/number.js'

export type Duration =
  | { minutes: number }
  | { hours: number }
  | { days: number }

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export const PROVET_DATEPICKER_DATE_FORMAT = 'yyyy-MM-dd'

/**
 * A human readable date format used across the UI.
 * @example
 * // "June 12, 2025"
 */
export const HUMAN_DATE_FORMAT = 'LLL d, yyyy'

// Array representing one minute, hour, day, week, month, etc in seconds
const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity]

// Array equivalent to the above but in the string representation of the units
const units: Intl.RelativeTimeFormatUnit[] = [
  'second',
  'minute',
  'hour',
  'day',
  'week',
  'month',
  'year',
]

/**
 * Get a relative time string, whilst attempting to use the most sensible unit of time.
 * Slightly modified from https://www.builder.io/blog/relative-time
 */
export function formatDateToRelativeTime(
  formatter: Intl.RelativeTimeFormat,
  date: Date,
) {
  // Allow dates or times to be passed
  const timeMs = typeof date === 'number' ? date : date.getTime()

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000)

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds),
  )

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1
  const unit = units[unitIndex]

  return formatter.format(Math.floor(deltaSeconds / divisor), unit)
}

/**
 * Converts a JavaScript Date object or an ISO 8601 date string to a specific format
 *
 * @param {Date | string} value - The JS Date object or date-time string in ISO 8601 format
 * @param {string} format - The desired string format, for instance 'YYYY-MM-DD'
 * @returns {string} - The date part of the input string in the provided format.
 */
export function formatDate(value: Date | string, format?: string) {
  const date = getDateTime(value)
  return format ? date.toFormat(format) : (date.toISO() ?? '')
}

/**
 * Converts a JavaScript Date object or an ISO 8601 date string to an ISO 8601 date-time string.
 *
 * @param {Date | string} date - The JavaScript Date object or date-time string in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @returns {string} - The ISO 8601 date-time string representation of the input date (YYYY-MM-DDTHH:mm:ss.sssZ)
 */
export function formatToDateTimeISO(date: Date | string) {
  return getDateTime(date).toISO() // YYYY-MM-DDTHH:mm:ss.sssZ
}

/**
 * Converts a JavaScript Date object or an ISO 8601 date string to an ISO 8601 date string.
 *
 * @param {Date | string} date - The JavaScript Date object or date-time string in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @returns {string} - The ISO 8601 date string representation of the input date (YYYY-MM-DD). Returns empty string if invalid.
 */
export function formatToDateTimeISODate(date: Date | string) {
  return getDateTime(date).toISODate() ?? '' // YYYY-MM-DD
}

/**
 * Converts a date string or Date object to a JavaScript Date object.
 * Useful when you need a Date object for formatters or other APIs that require Date objects.
 *
 * @param date - The date as a string (ISO, formatted, etc.) or Date object
 * @returns A JavaScript Date object
 *
 * @example
 * ```typescript
 * formatToJSDate('2024-01-15T10:30:00.000Z')
 * // Returns: Date object representing 2024-01-15T10:30:00.000Z
 *
 * formatToJSDate('2024-01-15')
 * // Returns: Date object representing 2024-01-15
 * ```
 */
export function formatToJSDate(date: Date | string): Date {
  if (date instanceof Date) {
    return date
  }
  return getDateTime(date).toJSDate()
}

/**
 * Takes a IANA timezone and a value and returns a valid local date and time string, or `undefined`.
 * https://data.iana.org/time-zones/tzdb-2021a/zone1970.tab
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Date_and_time_formats#local_date_and_time_strings
 * @param { Date | string | number } value - A value to convert to a valid `datetime-local` string.
 * @param { string } timezone - A IANA timezone.
 *
 * @returns - A string representing the date and time in the `datetime-local` format (e.g., "2024-01-15T10:30").
 *
 * @example
 * ```typescript
 * formatToDateTimeLocalString('2024-01-15T10:30:00.000Z', 'America/New_York')
 * // Returns: "2024-01-15T10:30"
 *
 * formatToDateTimeLocalString('2024-01-15T10:30:00.000Z', 'UTC')
 * // Returns: "2024-01-15T11:30"
 * ```
 */
export function formatToDateTimeLocalString(
  value?: Date | string | number,
  timezone?: string,
) {
  if (!value) {
    return ''
  }

  const date = timezone
    ? LuxonDateTime.fromJSDate(new Date(value), { zone: timezone })
    : LuxonDateTime.fromJSDate(new Date(value))
  if (!date.isValid) {
    console.error(date.invalidReason)
    console.error(date.invalidExplanation)
    return ''
  }
  const year = date.year
  const month = pad(date.month)
  const day = pad(date.day)
  const hours = pad(date.hour)
  const minutes = pad(date.minute)

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Converts a given date or string into a localized date string based on the specified locale and formatting options.
 *
 * @param value - The JavaScript Date object or date-time string in ISO 8601.
 * @param locale - The locale code (e.g., 'en-US', 'fr-FR') that determines the language.
 * @param formatOpts - An object specifying the formatting options. These options dictate how the date should be formatted (e.g., as a full date, a time, a short date, etc.).
 *
 * @returns - A string representing the date formatted according to the specified locale and formatting options (e.g., "Jun 12, 2025").
 *
 * @example
 * ```typescript
 * formatToDateLocaleString('2024-01-15T10:30:00.000Z', 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
 * // Returns: "Jan 15, 2024"
 * ```
 */
export function formatToDateLocaleString(
  value: Date | string,
  locale: string,
  formatOpts?: DateTimeFormatOptions,
) {
  return getDateTime(value)
    .setLocale(locale)
    .toLocaleString(
      formatOpts || {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      },
    )
}

/**
 * Formats a date as "22 Jan" (without year) - used for card subtitles.
 *
 * @param date - The JavaScript Date object or date-time string in ISO 8601.
 * @param locale - The locale code (e.g., 'en-US', 'fr-FR') that determines the language.
 * @returns A string representing the date formatted as day and month (e.g., "22 Jan").
 *
 * @example
 * ```typescript
 * formatDateToDMMM('2024-01-15T10:30:00.000Z', 'en-US')
 * // Returns: "15 Jan"
 * ```
 */
export function formatDateToDMMM(date: Date | string, locale: string) {
  return formatToDateLocaleString(date, locale, {
    day: 'numeric',
    month: 'short',
  })
}

/**
 * Converts a date string in a specified format to a date-time object.
 * Note: This function is not recommended for use with ISO 8601 dates.
 *
 * @param date - The date string to convert.
 * @param format - The format of the date string (e.g., 'yyyy-MM-dd', 'MM/dd/yyyy').
 * @returns An object representing the date-time object if the input is valid, otherwise undefined.
 */
export function formatToDateTimeObject(date: string, format: string) {
  const dateTime = LuxonDateTime.fromFormat(date, format)

  return dateTime.isValid
    ? {
        year: dateTime.year,
        month: dateTime.month,
        day: dateTime.day,
        hour: dateTime.hour,
        minute: dateTime.minute,
      }
    : undefined
}

/**
 * Formats a date string from an input format to a desired output format.
 *
 * @param value - The date string to be formatted.
 * @param inputFormat - The format in which the input date string is provided (e.g., "dd-MM-yyyy").
 * @param outputFormat - The desired format to which the date should be converted (e.g., "MMMM dd, yyyy").
 *
 * @returns - The formatted date string in the output format. Returns `null` if the date cannot be parsed.
 *
 * @example
 * ```typescript
 * formatDateTime('2024-01-15T10:30:00.000Z', 'yyyy-MM-dd HH:mm', 'MMMM dd, yyyy')
 * // Returns: "January 15, 2024"
 * ```
 */
export function formatDateTime(
  value: string,
  inputFormat: string,
  outputFormat: string,
) {
  const date = LuxonDateTime.fromFormat(value, inputFormat)
  return date.isValid ? date.toFormat(outputFormat) : null
}

/**
 * Parses an ISO date string and extracts the date and time.
 * @param isoString - The ISO date string to parse.
 * @returns An object containing the extracted date and time.
 */
export function parseISODate(isoString: string) {
  const dateTime = LuxonDateTime.fromISO(isoString)
  let time = ''
  let date = ''

  if (dateTime.isValid) {
    time = dateTime.toFormat(TimeFormat['HH:mm'])
    date = dateTime.toFormat('yyyy-MM-dd')
  }

  return { time, date }
}

/**
 * Converts a Duration object to milliseconds.
 *
 * @param duration - A duration object containing minutes, hours, or days
 * @returns The duration in milliseconds
 * @throws {Error} If the duration object is invalid or empty
 *
 * @example
 * ```typescript
 * formatDurationToMs({ minutes: 30 }) // Returns: 1800000 (30 minutes in ms)
 * formatDurationToMs({ hours: 2 }) // Returns: 7200000 (2 hours in ms)
 * formatDurationToMs({ days: 1 }) // Returns: 86400000 (1 day in ms)
 * ```
 */
export function formatDurationToMs(duration: Duration) {
  if ('minutes' in duration) {
    return duration.minutes * MINUTE
  }
  if ('hours' in duration) {
    return duration.hours * HOUR
  }
  if ('days' in duration) {
    return duration.days * DAY
  }

  throw new Error('Invalid duration')
}

/**
 * Formats an ISO timestamp to a human-readable string based on relative time.
 * - "Today" for the current day
 * - "Yesterday" for the day before
 * - The day of the week if within the last 7 days
 * - Otherwise, a full date format based on locale.
 * @param isoString - ISO date string to format.
 * @param labels - An object containing the labels for Today and Yesterday.
 *                 Example: { today: "Today", yesterday: "Yesterday" }
 * @param options - Optional configuration object.
 * @param options.compact - If true, displays simplified format:
 *                          - Today: only time (e.g., "2:30 PM")
 *                          - Yesterday: only "Yesterday"
 *                          - Older: formatted date (e.g., "1/13/2025")
 * @returns A string such as "Today, HH:mm" or "localised date format, HH:mm".
 * TODO: Rename this to formatTimestampToHuman
 */
export function timestampToHuman(
  isoString: string,
  labels: { today: string; yesterday: string },
  options?: { compact?: boolean },
) {
  const lastEditDate = LuxonDateTime.fromISO(isoString)

  if (!lastEditDate.isValid) {
    return ''
  }

  const now = LuxonDateTime.now()
  const isCompact = options?.compact === true

  // If edited today
  if (lastEditDate.hasSame(now, 'day')) {
    if (isCompact) {
      return lastEditDate.toLocaleString(LuxonDateTime.TIME_SIMPLE)
    }
    return `${labels.today}, ${lastEditDate.toLocaleString(LuxonDateTime.TIME_SIMPLE)}`
  }

  // If edited yesterday
  if (lastEditDate.plus({ days: 1 }).hasSame(now, 'day')) {
    if (isCompact) {
      return labels.yesterday
    }
    return `${labels.yesterday}, ${lastEditDate.toLocaleString(LuxonDateTime.TIME_SIMPLE)}`
  }

  // If edited within the last 7 days
  const daysAgo = now.diff(lastEditDate, 'days').days
  if (daysAgo <= 7) {
    if (isCompact) {
      return lastEditDate.toLocaleString(LuxonDateTime.DATE_SHORT)
    }
    return `${lastEditDate.toLocaleString({ weekday: 'long' })}, ${lastEditDate.toLocaleString(LuxonDateTime.TIME_SIMPLE)}`
  }

  // If edited more than 7 days ago, use a localized date format
  if (isCompact) {
    return lastEditDate.toLocaleString(LuxonDateTime.DATE_SHORT)
  }
  return `${lastEditDate.toLocaleString(LuxonDateTime.DATE_SHORT)}, ${lastEditDate.toLocaleString(LuxonDateTime.TIME_SIMPLE)}`
}

/**
 * Converts a duration object to a human-readable string.
 *
 * @param {DurationObjectUnits} duration - The duration object to convert.
 * @param {string} [smallestUnit='minutes'] - The smallest unit to include in the output string.
 * Valid values are: 'years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'.
 *
 * @returns {string} - The human-readable duration string.
 *
 * @example
 * durationToHuman({ years: 3, months: 4 }); // Output: "3 years, 4 months"
 * durationToHuman({ days: 1, hours: 2, minutes: 30 }, 'hours'); // Output: "26 hours, 30 minutes"
 * TODO: Rename this to formatDurationToHuman
 */
export function durationToHuman(
  duration: DurationObjectUnits,
  smallestUnit: LuxonDurationUnit = 'minutes',
  options?: ToHumanDurationOptions,
) {
  const units = [
    'years',
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
    'milliseconds',
  ] as LuxonDurationUnit[]
  const smallestIdx = units.indexOf(smallestUnit)
  const dur = LuxonDuration.fromObject(duration)

  const entries = Object.entries(
    dur
      .shiftTo(...units)
      .normalize()
      .toObject(),
  ).filter(([_unit, amount], idx) => amount > 0 && idx <= smallestIdx)

  const duration2 = LuxonDuration.fromObject(
    entries.length === 0 ? { [smallestUnit]: 0 } : Object.fromEntries(entries),
  )
  return duration2.toHuman(options)
}

/**
 * Combines a date string and time string into an ISO 8601 date-time string.
 *
 * @param date - The date string (e.g., "2024-01-15")
 * @param time - The time string (e.g., "10:30")
 * @param timezone - Optional IANA timezone (e.g., "Europe/Helsinki", "America/New_York").
 *                   If provided, interprets the date/time in this timezone before converting to ISO.
 *                   If not provided, uses the system's local timezone.
 * @returns An ISO 8601 date-time string (e.g., "2024-01-15T10:30:00.000+00:00")
 *          or empty string if the combination is invalid
 *
 * @example
 * ```typescript
 * combineDateAndTime('2024-01-15', '10:30')
 * // Returns: "2024-01-15T10:30:00.000+02:00" (in local timezone)
 *
 * combineDateAndTime('2024-01-15', '14:45', 'America/New_York')
 * // Returns: "2024-01-15T14:45:00.000-05:00" (in New York timezone)
 *
 * combineDateAndTime('invalid', '10:30')
 * // Returns: ""
 * ```
 */
export function combineDateAndTime(
  date: string,
  time: string,
  timezone?: string,
) {
  const dateTimeString = `${date}T${time}:00`

  if (timezone) {
    const dateTime = LuxonDateTime.fromISO(dateTimeString, { zone: timezone })
    return dateTime.toISO() ?? ''
  }

  return formatToDateTimeISO(dateTimeString) ?? ''
}

/**
 * Parses an abbreviated month name and year to create a date for the first day of that month.
 *
 * @param monthName - The abbreviated month name (e.g., 'Jan', 'Feb', 'Jul', 'Dec')
 * @param year - The year (e.g., 2025). Defaults to current year if not provided.
 * @param locale - Optional locale code (e.g., 'en-US'). Defaults to 'en-US'.
 * @returns An ISO date string for the first day of the month, or undefined if invalid
 *
 * @example
 * ```typescript
 * parseMonthNameToDate('Jul', 2025) // Returns: "2025-07-01T00:00:00.000+00:00"
 * parseMonthNameToDate('Jan') // Returns: "{currentYear}-01-01T00:00:00.000+00:00"
 * ```
 */
export function parseMonthNameToDate(
  monthName: string,
  year?: number,
  locale?: string,
) {
  const targetYear = year ?? LuxonDateTime.now().year
  const dateLocale = locale ?? 'en-US'
  const date = LuxonDateTime.fromFormat(
    `${monthName} 1, ${targetYear}`,
    'LLL d, yyyy',
    { locale: dateLocale },
  )
  return date.isValid ? (date.toISO() ?? undefined) : undefined
}

/**
 * Parses a date string in various formats and normalizes it to an ISO date string.
 * This function handles multiple date formats including European (dd/mm/yyyy) and US (mm/dd/yyyy) formats,
 * with different separators (/, -, .).
 *
 * @param inputValue - The date string to parse
 * @param formatPattern - The expected format pattern (e.g., 'dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy/MM/dd')
 * @returns The normalized ISO date string (yyyy-MM-dd) or undefined if parsing fails
 */
export function parseDateString(
  inputValue: string,
  formatPattern: SupportedLocaleDateFormats,
  outputFormat?: 'iso' | 'isoDate',
) {
  if (!inputValue || !formatPattern) {
    return undefined
  }

  const defaultOutputFormat = outputFormat ?? 'isoDate'
  const simplifiedFormatPattern = formatPattern.replace(/[./-]/g, '/')
  const validDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
  const validSeparators = ['.', '/', '-']
  const normalizedInput = inputValue.trim()

  // -- NORMALIZE INPUT --
  // Convert all different separators to a standard `/` separator
  // and extract any non-digit characters.
  const normalizedInputWithSeparators = [...normalizedInput].reduce(
    (accumulator, character) => {
      if (validSeparators.includes(character)) {
        return accumulator + '/'
      }

      // Only keep digits and ignore any other characters
      if (validDigits.includes(character)) {
        return accumulator + character
      }

      // Ignore any other characters
      return accumulator
    },
    '',
  )

  // -- PARSE EACH OF THE THREE DATE PARTS --
  const parts = normalizedInputWithSeparators.split('/')
  if (parts.length === 3) {
    let yearPart, monthPart, dayPart: string

    // DETERMINE POSITION OF YEAR, MONTH AND DAY BASED ON ALL DIFFERENT FORMAT PATTERNS
    if (simplifiedFormatPattern.startsWith('d')) {
      // Default format (dd/MM/yyyy)
      dayPart = parts[0]
      monthPart = parts[1]
      yearPart = parts[2]
    } else if (simplifiedFormatPattern.startsWith('M')) {
      // US format (MM/dd/yyyy)
      monthPart = parts[0]
      dayPart = parts[1]
      yearPart = parts[2]
    } else {
      // ISO format (yyyy/MM/dd)
      yearPart = parts[0]
      monthPart = parts[1]
      dayPart = parts[2]
    }

    // -- NORMALIZE YEAR --
    if (yearPart.length === 2) {
      // Two-digit year: add current century prefix
      // e.g. 25 -> 2025
      const currentCenturyPrefix = LuxonDateTime.now()
        .year.toString()
        .slice(0, 2)
      yearPart = `${currentCenturyPrefix}${yearPart}`
    } else if (yearPart.length > 4) {
      // Too long year: truncate to 4 digits.
      // e.g. 20250101 -> 2025
      yearPart = yearPart.slice(0, 4)
    } else if (yearPart.length === 3) {
      // Edge case with three-digit year: add missing digit (assume 0)
      // e.g. 202 -> 2020
      yearPart = `${yearPart}0`
    }

    // -- PARSE DATE AND VALIDATE --
    // Parse the date string to a Luxon DateTime object and validate it with
    // `isValid` to make sure the date has been correctly parsed.
    let dateTime = LuxonDateTime.fromFormat(
      `${dayPart}/${monthPart}/${yearPart}`,
      'd/M/yyyy',
    )

    // -- EDGE CASE: MONTH > 12 --
    // If the month is greater than 12, validate as if the day is the month.
    // to make sure the date is valid and was simply misinterpreted in a wrong format.
    // e.g. 31/01/2025 is invalid for dd/MM/yyyy, but valid for MM/dd/yyyy.
    // Note: If the MM/dd/yyyy date is valid, it will still be returned as ISO date format.
    if (
      !dateTime.isValid &&
      parseInt(monthPart) > 12 &&
      parseInt(dayPart) <= 12
    ) {
      dateTime = LuxonDateTime.fromFormat(
        `${dayPart}/${monthPart}/${yearPart}`,
        'M/d/yyyy',
      )
    }

    // Return ISO date if valid
    if (dateTime.isValid) {
      return dateTime.toISODate()
    }
  }

  // Fall back to direct parsing
  const dateTimeFallback = LuxonDateTime.fromFormat(
    normalizedInputWithSeparators,
    simplifiedFormatPattern,
  )

  if (!dateTimeFallback.isValid) {
    return
  }

  if (defaultOutputFormat === 'isoDate') {
    return dateTimeFallback.toISODate()
  }

  return dateTimeFallback.toISO()
}
